import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Megaphone, Plus, Edit, Trash2, Calendar, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SearchBar from '@/components/shared/SearchBar';
import { API, toast } from '@/App';
import { getAuthToken } from '@/utils/authSession';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    link_url: '',
    link_text: '',
    background_color: '#3b82f6',
    text_color: '#ffffff',
    start_date: '',
    end_date: '',
    is_active: true,
    priority: 1
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/admin/banners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des bannières');
    } finally {
      setLoading(false);
    }
  };

  const filteredBanners = banners.filter(banner =>
    banner.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    banner.message?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeBanners = filteredBanners.filter(b => b.is_active && new Date(b.end_date) > new Date());
  const scheduledBanners = filteredBanners.filter(b => b.is_active && new Date(b.start_date) > new Date());
  const expiredBanners = filteredBanners.filter(b => new Date(b.end_date) <= new Date());

  const handleCreate = () => {
    setEditingBanner(null);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    setFormData({
      title: '',
      message: '',
      link_url: '',
      link_text: '',
      background_color: '#3b82f6',
      text_color: '#ffffff',
      start_date: now.toISOString().split('T')[0],
      end_date: tomorrow.toISOString().split('T')[0],
      is_active: true,
      priority: 1
    });
    setShowDialog(true);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      ...banner,
      start_date: banner.start_date.split('T')[0],
      end_date: banner.end_date.split('T')[0]
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.message || !formData.start_date || !formData.end_date) {
      toast.error('Message et dates sont obligatoires');
      return;
    }

    try {
      const token = getAuthToken();
      const data = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      };

      if (editingBanner) {
        await axios.patch(`${API}/admin/banners/${editingBanner.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bannière modifiée avec succès');
      } else {
        await axios.post(`${API}/admin/banners`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bannière créée avec succès');
      }

      setShowDialog(false);
      loadBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      const token = getAuthToken();
      await axios.patch(
        `${API}/admin/banners/${banner.id}`,
        { is_active: !banner.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(banner.is_active ? 'Bannière désactivée' : 'Bannière activée');
      loadBanners();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer cette bannière ?`)) return;

    try {
      const token = getAuthToken();
      await axios.delete(`${API}/admin/banners/${banner.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bannière supprimée avec succès');
      loadBanners();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const BannerPreview = ({ banner }) => (
    <div
      className="p-4 rounded-lg mb-3"
      style={{
        backgroundColor: banner.background_color,
        color: banner.text_color
      }}
    >
      {banner.title && <h4 className="font-bold mb-1">{banner.title}</h4>}
      <p className="text-sm">{banner.message}</p>
      {banner.link_url && (
        <a
          href={banner.link_url}
          className="text-sm underline mt-2 inline-block"
          style={{ color: banner.text_color }}
        >
          {banner.link_text || 'En savoir plus'}
        </a>
      )}
    </div>
  );

  const BannerTable = ({ banners: bannersList }) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Titre</TableHead>
          <TableHead>Message</TableHead>
          <TableHead>Période</TableHead>
          <TableHead>Priorité</TableHead>
          <TableHead>Aperçu</TableHead>
          <TableHead>Statut</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {bannersList.map((banner) => (
          <TableRow key={banner.id}>
            <TableCell className="font-medium">{banner.title || '-'}</TableCell>
            <TableCell className="max-w-xs truncate">{banner.message}</TableCell>
            <TableCell className="text-sm">
              <div>
                <p>Du: {new Date(banner.start_date).toLocaleDateString('fr-FR')}</p>
                <p>Au: {new Date(banner.end_date).toLocaleDateString('fr-FR')}</p>
              </div>
            </TableCell>
            <TableCell>
              <Badge variant="outline">{banner.priority}</Badge>
            </TableCell>
            <TableCell>
              <div
                className="w-6 h-6 rounded border-2 border-gray-300"
                style={{ backgroundColor: banner.background_color }}
                title={`Couleur: ${banner.background_color}`}
              />
            </TableCell>
            <TableCell>
              <Switch
                checked={banner.is_active}
                onCheckedChange={() => handleToggleActive(banner)}
              />
            </TableCell>
            <TableCell>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(banner)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(banner)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Bannières Promotionnelles</h2>
          <p className="text-gray-600 mt-1">
            Gérez les bannières affichées sur la page d'accueil
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Créer une bannière
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold gradient-text">{activeBanners.length}</p>
              <p className="text-sm text-gray-600">Bannières actives</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{scheduledBanners.length}</p>
              <p className="text-sm text-gray-600">Programmées</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-400">{expiredBanners.length}</p>
              <p className="text-sm text-gray-600">Expirées</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Banners Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Bannières configurées</CardTitle>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Rechercher une bannière..."
              className="max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="active">
            <TabsList>
              <TabsTrigger value="active">Actives ({activeBanners.length})</TabsTrigger>
              <TabsTrigger value="scheduled">Programmées ({scheduledBanners.length})</TabsTrigger>
              <TabsTrigger value="expired">Expirées ({expiredBanners.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="active">
              {activeBanners.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune bannière active</p>
                </div>
              ) : (
                <BannerTable banners={activeBanners} />
              )}
            </TabsContent>
            <TabsContent value="scheduled">
              {scheduledBanners.length === 0 ? (
                <p className="text-center py-12 text-gray-500">Aucune bannière programmée</p>
              ) : (
                <BannerTable banners={scheduledBanners} />
              )}
            </TabsContent>
            <TabsContent value="expired">
              {expiredBanners.length === 0 ? (
                <p className="text-center py-12 text-gray-500">Aucune bannière expirée</p>
              ) : (
                <BannerTable banners={expiredBanners} />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBanner ? 'Modifier la bannière' : 'Créer une bannière'}
            </DialogTitle>
            <DialogDescription>
              Configurez le contenu et l'apparence de la bannière
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Preview */}
            <div>
              <Label>Aperçu en temps réel</Label>
              <BannerPreview banner={formData} />
            </div>

            {/* Form Fields */}
            <div>
              <Label>Titre (optionnel)</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: 🎉 Offre spéciale de lancement"
              />
            </div>

            <div>
              <Label>Message *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Ex: Profitez de -20% sur tous les abonnements Express jusqu'au 31 décembre!"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lien (optionnel)</Label>
                <Input
                  value={formData.link_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Texte du lien</Label>
                <Input
                  value={formData.link_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, link_text: e.target.value }))}
                  placeholder="En savoir plus"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Couleur de fond</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.background_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={formData.background_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                    placeholder="#3b82f6"
                  />
                </div>
              </div>
              <div>
                <Label>Couleur du texte</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.text_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={formData.text_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Date de fin *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <Label>Priorité d'affichage</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                min="1"
                max="10"
              />
              <p className="text-xs text-gray-500 mt-1">
                Plus le nombre est élevé, plus la bannière sera affichée en priorité (1-10)
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-semibold">Activer la bannière</p>
                <p className="text-sm text-gray-600">
                  La bannière sera visible sur le site
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData(prev => ({ ...prev, is_active: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit}>
              {editingBanner ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannerManagement;
